import { LayoutService } from '@/app/layout/service/layout.service';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    computed,
    inject,
    signal
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';

const SCROLL_PAGE_RATIO = 0.8;
const SCROLL_EDGE_THRESHOLD = 2;

@Component({
    selector: '[app-menu-scroller]',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    host: {
        class: 'layout-menu-scroller-host'
    },
    template: `
        <nav class="layout-menu-nav" [attr.aria-label]="'MENU.NAV_ARIA' | translate">
            <button
                type="button"
                class="layout-menu-scroll-btn layout-menu-scroll-btn--prev"
                [class.layout-menu-scroll-btn--active]="canScrollLeft()"
                [class.layout-menu-scroll-btn--visible]="shouldShowScrollButtons()"
                [disabled]="!canScrollLeft()"
                [attr.aria-label]="'MENU.SCROLL_LEFT_ARIA' | translate"
                [attr.aria-hidden]="!shouldShowScrollButtons()"
                aria-controls="layout-menu-viewport"
                (click)="scrollPage('left')"
            >
                <i class="pi pi-chevron-left" aria-hidden="true"></i>
            </button>

            <div
                class="layout-menu-viewport"
                [class.layout-menu-viewport--fade-left]="hasOverflow() && showScrollControls() && canScrollLeft()"
                [class.layout-menu-viewport--fade-right]="hasOverflow() && showScrollControls() && canScrollRight()"
            >
                <div
                    #menuContainer
                    id="layout-menu-viewport"
                    class="layout-menu-container layout-menu-viewport-track"
                    (scroll)="onMenuScroll()"
                    (keydown)="onViewportKeydown($event)"
                    tabindex="-1"
                >
                    <ng-content />
                </div>
            </div>

            <button
                type="button"
                class="layout-menu-scroll-btn layout-menu-scroll-btn--next"
                [class.layout-menu-scroll-btn--active]="canScrollRight()"
                [class.layout-menu-scroll-btn--visible]="shouldShowScrollButtons()"
                [disabled]="!canScrollRight()"
                [attr.aria-label]="'MENU.SCROLL_RIGHT_ARIA' | translate"
                [attr.aria-hidden]="!shouldShowScrollButtons()"
                aria-controls="layout-menu-viewport"
                (click)="scrollPage('right')"
            >
                <i class="pi pi-chevron-right" aria-hidden="true"></i>
            </button>

            <span class="layout-menu-scroll-status" aria-live="polite">{{ scrollStatusMessage() }}</span>
        </nav>
    `
})
export class AppMenuScroller implements AfterViewInit, OnDestroy {
    readonly layoutService = inject(LayoutService);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);

    @ViewChild('menuContainer') menuContainer!: ElementRef<HTMLElement>;

    readonly canScrollLeft = signal(false);
    readonly canScrollRight = signal(false);
    readonly hasOverflow = signal(false);
    readonly scrollStatusMessage = signal('');

    readonly showScrollControls = computed(() => this.layoutService.isHorizontal() && this.layoutService.isDesktop());
    readonly shouldShowScrollButtons = computed(() => this.showScrollControls() && this.hasOverflow());

    private resizeObserver: ResizeObserver | null = null;
    private mutationObserver: MutationObserver | null = null;
    private scrollEndTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly destroy$ = new Subject<void>();

    ngAfterViewInit(): void {
        this.scheduleScrollStateUpdate();
        this.setupResizeObserver();
        this.setupMutationObserver();
        this.scrollActiveItemIntoView();

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                setTimeout(() => {
                    this.scheduleScrollStateUpdate();
                    this.scrollActiveItemIntoView();
                });
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        if (this.scrollEndTimer) {
            clearTimeout(this.scrollEndTimer);
        }
    }

    getContainerElement(): HTMLElement | null {
        return this.menuContainer?.nativeElement ?? null;
    }

    scrollPage(direction: 'left' | 'right'): void {
        const el = this.menuContainer?.nativeElement;
        if (!el) {
            return;
        }

        const pageSize = el.clientWidth * SCROLL_PAGE_RATIO;
        const delta = direction === 'left' ? -pageSize : pageSize;
        el.scrollBy({ left: delta, behavior: 'smooth' });
        this.scheduleScrollStateUpdate(350);
    }

    scrollActiveItemIntoView(): void {
        const el = this.menuContainer?.nativeElement;
        if (!el || !this.layoutService.isHorizontal()) {
            return;
        }

        const activeItem = el.querySelector('.layout-root-menuitem.active-menuitem') as HTMLElement | null;
        if (!activeItem) {
            return;
        }

        activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        setTimeout(() => this.updateScrollState(), 300);
    }

    onMenuScroll(): void {
        const el = this.menuContainer?.nativeElement;
        if (!el) {
            return;
        }

        if (this.layoutService.isHorizontal()) {
            const scrollLeft = el.scrollLeft;
            el.style.setProperty('--menu-scroll-x', `-${scrollLeft}px`);
        } else {
            const scrollTop = el.scrollTop;
            el.style.setProperty('--menu-scroll-y', `-${scrollTop}px`);
        }

        this.updateScrollState();

        // Do not close open submenus on scroll — it breaks dropdown visibility in the horizontal menu.
    }

    onViewportKeydown(event: KeyboardEvent): void {
        if (!this.shouldShowScrollButtons()) {
            return;
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.scrollPage('left');
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.scrollPage('right');
        }
    }

    updateScrollState(): void {
        const el = this.menuContainer?.nativeElement;
        if (!el) {
            return;
        }

        const menuList = el.querySelector('.layout-menu') as HTMLElement | null;
        const scrollWidth = menuList?.scrollWidth ?? el.scrollWidth;
        const clientWidth = el.clientWidth;
        const scrollLeft = el.scrollLeft;
        const overflow = scrollWidth - clientWidth > SCROLL_EDGE_THRESHOLD;

        this.hasOverflow.set(overflow);
        this.canScrollLeft.set(overflow && scrollLeft > SCROLL_EDGE_THRESHOLD);
        this.canScrollRight.set(overflow && scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_THRESHOLD);

        if (overflow && this.shouldShowScrollButtons()) {
            const key = this.canScrollRight()
                ? 'MENU.SCROLL_STATUS_MORE_RIGHT'
                : this.canScrollLeft()
                  ? 'MENU.SCROLL_STATUS_MORE_LEFT'
                  : 'MENU.SCROLL_STATUS_ALL_VISIBLE';
            this.scrollStatusMessage.set(this.translate.instant(key));
        } else {
            this.scrollStatusMessage.set('');
        }
    }

    private scheduleScrollStateUpdate(delayMs = 0): void {
        if (this.scrollEndTimer) {
            clearTimeout(this.scrollEndTimer);
        }

        this.scrollEndTimer = setTimeout(() => this.updateScrollState(), delayMs);
    }

    private setupResizeObserver(): void {
        const el = this.menuContainer?.nativeElement;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.scheduleScrollStateUpdate();
        });

        this.resizeObserver.observe(el);
        const menuList = el.querySelector('.layout-menu');
        if (menuList) {
            this.resizeObserver.observe(menuList);
        }

        const host = el.closest('.layout-menu-scroller-host');
        if (host) {
            this.resizeObserver.observe(host);
        }
    }

    private setupMutationObserver(): void {
        const el = this.menuContainer?.nativeElement;
        if (!el || typeof MutationObserver === 'undefined') {
            return;
        }

        this.mutationObserver = new MutationObserver(() => {
            this.scheduleScrollStateUpdate();
        });

        this.mutationObserver.observe(el, { childList: true, subtree: true });
    }
}
